/**
 * Bounded Context: Initiative — UI Constraints Layer (TS-side)
 *
 * ⚠️ GATE CONSTITUCIONAL ⚠️
 *
 * Esta camada implementa **UI CONSTRAINTS** sobre o enum canônico R1-R6.
 * NÃO redefine R1-R6 — eles vêm do codegen Python (HLOS-112/113).
 *
 * A fonte canônica de R1-R6 é `InitiativePolicy.should_initiate()` em
 * `hlos/kernel/runtime/initiative.py` (Python) — o string `decision.reason`
 * é exposto cross-runtime via `hlos_kernel.bridge._extract_r_level`.
 * O enum TS é gerado mecanicamente em `./generated.ts` (HLOS-113).
 *
 * Esta camada (`ui-constraints.ts`) adiciona **regras de APRESENTAÇÃO** no
 * lado TypeScript:
 *
 * | R | Trigger Python (canônico) | UI constraint TS-side (este arquivo) |
 * |---|---|---|
 * | R1 | `learner_initiative=system AND mastery_initial<0.3` | **Uma** sugestão suave por turno (limite frequência) |
 * | R2 | `learner_initiative=learner AND mastery_persistent>0.8 AND clarity>0.8` | Provocação só após pedido do aprendiz |
 * | R3 | `last_action=ASK` | Sem iniciativa pendente; aguardar |
 * | R4 | *(gap intencional — HLOS futuro)* | System initiative com baixa confiança: espera estruturada (v1 sem background cognition) |
 * | R5 | `last_action=PAUSE` | Toda iniciativa é convite, não instrução; copy neutra |
 * | R6 | `last_action=SKIP OR denial_policy=blocked OR default` | Zero push notification (D5); só mostra na UI ativa |
 *
 * **HLOS-114 refactor:** Interface `Initiative` (TS-only, NÃO vem do codegen)
 * é a "saída" da função `decideInitiative`. Difere de `InitiativeDecision`
 * (que vem do Python source via codegen) — `Initiative` é uma view leve
 * para consumers TS (kind/rLevel/text), enquanto `InitiativeDecision` é
 * o dataclass Python espelhado no TS.
 *
 * **Mudanças neste arquivo** devem:
 * 1. Ser coerentes com a tabela canônica acima (não redefinir R1-R6)
 * 2. Refletir **limites de UI** (frequência, copy, visibilidade), não política
 * 3. Importar `RLevel`, `InitiativeKind` do enum gerado em `./generated.ts`
 *
 * **Pre-commit hook** (HLOS-114 AC-3): edits manuais em `./rules.ts` são
 * bloqueados. Para mudar lógica UI, edite **este arquivo** (`ui-constraints.ts`).
 * Para mudar R1-R6 em si, edite o Python source `InitiativePolicy.should_initiate()`
 * e rode `python -m tools.codegen_initiative`.
 *
 * Refs:
 *   - ADR-0022 (Python canônico + codegen TS)
 *   - ADR-0023 (canonicidade semântica R1-R6)
 *   - ADR-0025 (toolchain custom AST-based)
 *   - HLOS-112 (POC), HLOS-113 (produção), HLOS-114 (este refactor)
 */

import type { InitiativeKind, RLevel } from "./generated";

/**
 * Output type da função `decideInitiative` (TS-only).
 *
 * Difere de `InitiativeDecision` (que vem do codegen Python↔TS via
 * `python -m tools.codegen_initiative`). `Initiative` é uma view leve
 * para consumers TS — `kind` é o `InitiativeKind` (7 valores do enum Python
 * `InitiativeType(str, Enum)`), `rLevel` é o R-level (`"R1"`..`"R6"`).
 *
 * `text: null` significa iniciativa silenciada (sem copy para mostrar).
 */
export interface Initiative {
  /** Tipo de iniciativa (do enum Python canônico). */
  readonly kind: InitiativeKind;
  /** R-level canônico (do string `decision.reason` Python). */
  readonly rLevel: RLevel;
  /** Texto opcional da iniciativa (null para silent/none). */
  readonly text: string | null;
}

/** Estado do aprendiz relevante para decidir o nível de iniciativa. */
export interface InitiativeContext {
  /** O usuário pediu explicitamente uma provocação? (true após aceitar R1) */
  readonly userRequestedProvocation: boolean;
  /** Há gaps de curiosidade abertos? (R2 só faz sentido com gaps) */
  readonly hasCuriosityGaps: boolean;
  /** O usuário está presente na UI? (R6 — sem push) */
  readonly userInUi: boolean;
  /** HLOS-102 (AC-4): paridade Python↔TS — espelha `learner_initiative` do runtime. */
  readonly initiative?: "system" | "learner" | "shared";
  /** HLOS-102 (AC-4): confiança do sistema (0-1) — alimenta o R4 (hesitação estruturada). */
  readonly confidence?: number;
}

/**
 * Decide o tipo de iniciativa permitida neste turno.
 *
 * Regra de ouro: **se houver dúvida, retornar silent**. Iniciativa é
 * responsabilidade, não default.
 *
 * Contrato: SEMPRE retorna um {@link Initiative} válido (per HLOS-19 AC:
 * "nunca retorna proposta inválida"). Use {@link decideInitiativeOrNull}
 * quando precisar da forma `Initiative | null` (null = sem decisão ativa).
 *
 * **HLOS-114 refactor:** Esta função agora retorna `kind: "none"` em vez de
 * `kind: "none"` (que existia na versão antiga com InitiativeKind de 4 valores).
 * O enum canônico (7 valores do codegen) tem `NONE = "none"` para indicar
 * "Companion não toma iniciativa neste turno". Alinhado com Python.
 */
export function decideInitiative(ctx: InitiativeContext): Initiative {
  // Defaults seguros para campos opcionais (HLOS-102).
  // initiative default "shared" (não é system, então R4 não dispara por default).
  // confidence default 1.0 (alta, então R4 não dispara por default).
  const initiative = ctx.initiative ?? "shared";
  const confidence = ctx.confidence ?? 1.0;

  // R6: zero push; só agir quando usuário está na UI.
  if (!ctx.userInUi) {
    return { kind: "none", rLevel: "R6", text: null };
  }
  // R4: system initiative AND low confidence (e nada para agir).
  // HLOS-102 (AC-4): paridade Python↔TS — "espera estruturada" quando
  // o sistema tem dúvida (low confidence) e não há gap/provocation.
  // v1: sem background cognition (D4 do Companion Runtime).
  if (
    initiative === "system"
    && confidence < 0.5
    && !ctx.userRequestedProvocation
    && !ctx.hasCuriosityGaps
  ) {
    return { kind: "none", rLevel: "R4", text: null };
  }
  // R3: sem iniciativa pendente se não há sinal do usuário.
  // R4: sem sinal = sem iniciativa autônoma.
  if (!ctx.userRequestedProvocation && !ctx.hasCuriosityGaps) {
    return { kind: "none", rLevel: "R3", text: null };
  }
  // R2: provocação só após pedido explícito (R1 → R2).
  if (ctx.userRequestedProvocation && ctx.hasCuriosityGaps) {
    return {
      kind: "provocation",
      rLevel: "R2",
      text: "Quer explorar uma das lacunas em aberto?",
    };
  }
  // R1: oferta suave (máx. uma por turno).
  if (ctx.hasCuriosityGaps) {
    return {
      kind: "gentle_offer",
      rLevel: "R1",
      text: "Posso oferecer uma direção de estudo opcional?",
    };
  }
  // R5: silent é sempre convite, nunca ordem.
  return { kind: "none", rLevel: "R5", text: null };
}

/**
 * Variante de {@link decideInitiative} que devolve `Initiative | null`
 * (per HLOS-19 AC: "decideInitiative(state) retorna proposta válida OU null").
 *
 * `null` significa: "Companion **não** toma iniciativa neste turno" — equivale
 * ao `kind: "none"` da forma canônica, mas torna explícita a ausência de
 * decisão ativa. Útil para consumers que querem esconder UI quando não há
 * iniciativa (em vez de checar `kind === "none"`).
 *
 * Regra de ouro: nunca retorna uma `Initiative` inválida.
 */
export function decideInitiativeOrNull(ctx: InitiativeContext): Initiative | null {
  const decision = decideInitiative(ctx);
  if (decision.kind === "none") return null;
  // Defesa em profundidade — isValidInitiative deve passar sempre aqui.
  if (!isValidInitiative(decision)) return null;
  return decision;
}

/** Mapeamento R-Level → descrição legível (auditoria CC / Constitutional Guardian). */
export const R_LEVEL_DESCRIPTIONS: Readonly<Record<RLevel, string>> = Object.freeze({
  R1: "Oferta suave — uma sugestão por turno, opcional.",
  R2: "Provocação intelectual — só após pedido do aprendiz.",
  R3: "Sem iniciativa — aguarda sinal do aprendiz.",
  R4: "System initiative com baixa confiança — espera estruturada (v1 sem background cognition).",
  R5: "Convite, nunca instrução — copy neutra.",
  R6: "Zero push — iniciativa só aparece na UI ativa.",
});

/** Sanidade: nunca retornar iniciativa vazia com nível ≠ none. */
export function isValidInitiative(init: Initiative): boolean {
  if (init.kind === "none") {
    return init.text === null;
  }
  // Tipos com copy (gentle_offer, provocation) precisam de text não-vazio
  return typeof init.text === "string" && init.text.length > 0;
}

/**
 * Mapeamento inverso: tipo de iniciativa → R-level esperado.
 *
 * HLOS-114: alinhado com `InitiativeKind` canônico (7 valores do codegen).
 * Os 3 valores "estruturais" (scaffolding/challenge/step_back) mapeiam
 * para R4-R6 (espelhando `runtime.py:_initiative_to_r_level` — ver ADR-0023).
 * Valores com copy (gentle_offer/provocation) mapeiam para R1-R2.
 */
export const EXPECTED_R_LEVEL: Readonly<Record<InitiativeKind, RLevel>> = Object.freeze({
  gentle_offer: "R1",
  provocation: "R2",
  none: "R3", // default — pode ser R3/R4/R5/R6 dependendo do contexto
  scaffolding: "R4",
  challenge: "R5",
  step_back: "R6",
  curiosity_gap: "R2", // similar a provocation (per runtime.py)
});
